import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Poster from './Poster';
import RankingTable from './RankingTable';
import useWindowWidth from '../hooks/useWindowWidth';

/**
 * Building blocks for the branches pages. They follow CLAUDE.md §0: whole
 * numbers only and short titles instead of explanations. Rankings are
 * RankingTable (cards on a phone, like every other page); the only bars are
 * the "מה מצליח כאן" ranking and the daily chart.
 */

const fmt = (v) => Math.round(v || 0).toLocaleString();
const dm = (iso) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;

export const card = { background: 'var(--surf)', borderRadius: 16, boxShadow: 'var(--shadow)', padding: 16 };
export const cardTitle = { fontSize: 15, fontWeight: 700, color: 'var(--txt)', marginBottom: 12 };

/** "מול 17–20.09": the period every change on the page is measured against. */
export function ComparedTo({ range }) {
  if (!range || !range.prev_start) return null;
  const span = range.prev_start === range.prev_end
    ? dm(range.prev_start) : `${dm(range.prev_start)}–${dm(range.prev_end)}`;
  return (
    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
      מול <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{span}</span>
    </div>
  );
}

/** ↑12% / ↓8% against the previous period; "—" when there is none. */
export function Change({ value, size = 12 }) {
  if (value == null) return <span style={{ fontSize: size, color: 'var(--faint)' }}>—</span>;
  const up = value >= 0;
  return (
    <span dir="ltr" style={{ unicodeBidi: 'isolate', fontSize: size, fontWeight: 700, whiteSpace: 'nowrap',
                             color: up ? 'var(--ok)' : 'var(--danger)' }}>
      {up ? '↑' : '↓'}{Math.abs(value)}%
    </span>
  );
}

/** A row of 3–4 figures. Two per line on a phone when there are four. */
export function KpiRow({ items }) {
  const mob = useWindowWidth() < 768;
  const cols = mob && items.length === 4 ? 2 : items.length;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: mob ? 8 : 14 }}>
      {items.map((it) => (
        <div key={it.label} style={{ ...card, padding: mob ? '12px 12px' : '16px 20px' }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>{it.label}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            <span style={{ fontSize: mob ? 20 : 26, fontWeight: 800, color: 'var(--txt)',
                           fontVariantNumeric: 'tabular-nums' }}>{it.value}</span>
            {'change' in it && <Change value={it.change} />}
          </div>
          {it.sub && <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 2 }}>{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

/** Segmented tabs. Scrolls sideways on a narrow screen rather than wrapping. */
export function Tabs({ value, onChange, options }) {
  return (
    <div style={{ display: 'flex', gap: 4, background: 'var(--surf-alt)', borderRadius: 10, padding: 4,
                  width: 'fit-content', maxWidth: '100%', overflowX: 'auto' }}>
      {options.map(o => (
        <button key={o.key} onClick={() => onChange(o.key)} style={{
          padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
          fontFamily: 'Heebo, sans-serif', fontSize: 13, fontWeight: 600,
          background: value === o.key ? 'var(--surf)' : 'transparent',
          color: value === o.key ? 'var(--accent)' : 'var(--muted)',
          boxShadow: value === o.key ? 'var(--shadow-subtle)' : 'none',
        }}>{o.label}</button>
      ))}
    </div>
  );
}

const moreBtn = {
  marginTop: 8, width: '100%', padding: '8px 0', border: '1px solid var(--brd)', borderRadius: 10,
  background: 'transparent', color: 'var(--accent)', fontFamily: 'Heebo, sans-serif', fontSize: 13,
  fontWeight: 600, cursor: 'pointer',
};

export function MoreButton({ onClick, children = 'הצג הכל' }) {
  return <button onClick={onClick} style={moreBtn}>{children}</button>;
}

const nameCell = (name, sub, poster) => (r) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
    {poster && <Poster posterUrl={r.poster_url} genre={r.genre} letter={(name(r) || '?')[0]} size="sm" />}
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--txt)', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name(r)}</div>
      {sub && sub(r) && (
        <div style={{ fontSize: 11, color: 'var(--faint)', overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' }}>{sub(r)}</div>
      )}
    </div>
  </div>
);

const pct = (v) => (v == null ? '—' : `${v}%`);
const num = (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(v)}</span>;

/** Ready-made columns for the tables on these pages. Whole numbers, no bars. */
export const col = {
  name: (header, name, sub, poster) => ({
    key: 'name', header, align: 'right', render: nameCell(name, sub, poster), sortValue: name,
  }),
  tickets: { key: 'tickets', header: 'כרטיסים', align: 'center', mobilePrimary: true,
             render: (r) => <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(r.tickets)}</b> },
  change: { key: 'change', header: 'שינוי', align: 'center', render: (r) => <Change value={r.change} />,
            sortValue: (r) => (r.change == null ? -Infinity : r.change) },
  share: (key = 'market_share', header = 'נתח שוק') => ({
    key, header, align: 'center', render: (r) => pct(r[key]),
  }),
  screenings: { key: 'screenings', header: 'הקרנות', align: 'center', render: (r) => num(r.screenings) },
  cinemas: { key: 'cinemas', header: 'סניפים', align: 'center', render: (r) => num(r.cinemas) },
  occupancy: { key: 'occupancy', header: 'תפוסה', align: 'center', render: (r) => pct(r.occupancy) },
};

/**
 * A titled ranking table: a table on a desktop, one card per row on a phone
 * (RankingTable does the switch), sortable and paged.
 */
export function VenueTable({ title, rows, columns, onClick, pageSize = 10 }) {
  return (
    <div>
      {title && <div style={{ ...cardTitle, marginBottom: 8 }}>{title}</div>}
      {rows.length ? (
        <RankingTable columns={columns} data={rows} rankColumn minWidth={560}
                      mobileStatCols={Math.min(4, Math.max(1, columns.length - 2))}
                      pageSize={rows.length > pageSize ? pageSize : undefined}
                      initialSort={{ key: 'tickets', dir: 'desc' }}
                      onRowClick={onClick} />
      ) : (
        <div style={{ ...card, fontSize: 13, color: 'var(--faint)' }}>אין נתונים בטווח</div>
      )}
    </div>
  );
}

/** Categories by how full they were: label, bar, "68%". One colour. */
export function OccupancyBars({ rows }) {
  if (!rows.length) return <div style={{ fontSize: 13, color: 'var(--faint)' }}>אין מספיק הקרנות</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map(r => (
        <div key={r.key} title={`${r.screenings} הקרנות · ${fmt(r.tickets)} כרטיסים`}
             style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 96, flexShrink: 0, fontSize: 13, color: 'var(--txt)', overflow: 'hidden',
                         textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
          <div style={{ flex: 1, height: 10, borderRadius: 5, background: 'var(--surf-alt)' }}>
            <div style={{ width: `${Math.min(100, r.occupancy)}%`, height: '100%', borderRadius: 5,
                          background: 'var(--accent)', minWidth: r.occupancy > 0 ? 4 : 0 }} />
          </div>
          <span style={{ width: 38, flexShrink: 0, textAlign: 'left', fontSize: 13, fontWeight: 700,
                         color: 'var(--txt)', fontVariantNumeric: 'tabular-nums' }}>{r.occupancy}%</span>
        </div>
      ))}
    </div>
  );
}

const tooltipStyle = {
  contentStyle: { background: 'var(--surf)', border: 'none', borderRadius: 12, boxShadow: 'var(--shadow)',
                  color: 'var(--txt)', fontFamily: 'Heebo,sans-serif', fontSize: 13, padding: '8px 14px' },
  labelStyle: { color: 'var(--txt)', fontWeight: 600, marginBottom: 2 },
  cursor: { fill: 'var(--surf-hov)' },
};

/** Tickets per day across the range. */
export function DailyBars({ rows }) {
  const mob = useWindowWidth() < 768;
  const data = rows.map(r => ({ ...r, label: rows.length <= 7 ? r.weekday : dm(r.date) }));
  return (
    <ResponsiveContainer width="100%" height={mob ? 160 : 200}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--brd)" vertical={false} />
        <XAxis dataKey="label" tick={{ fill: 'var(--faint)', fontSize: 11 }} axisLine={false} tickLine={false}
               interval="preserveStartEnd" />
        <YAxis hide={mob} tick={{ fill: 'var(--faint)', fontSize: 10 }} axisLine={false} tickLine={false}
               width={40} tickFormatter={fmt} />
        <Tooltip {...tooltipStyle} labelFormatter={(l, p) => (p && p[0] ? `${p[0].payload.weekday} ${dm(p[0].payload.date)}` : l)}
                 formatter={(v) => [`${fmt(v)} כרטיסים`, '']} separator="" />
        <Bar dataKey="tickets" fill="var(--accent)" radius={[4, 4, 0, 0]} maxBarSize={36} />
      </BarChart>
    </ResponsiveContainer>
  );
}
